import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/common/StatusBadge';
import { FileText, Receipt, Wrench, LogOut, CreditCard, Droplets } from 'lucide-react';
import { toast } from 'sonner';

const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export default function ContractorPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [e, i, j] = await Promise.all([
        api.get('/estimates', { params: { page_size: 100 } }),
        api.get('/invoices', { params: { page_size: 100 } }),
        api.get('/jobs', { params: { page_size: 100 } }),
      ]);
      setEstimates(e.data.items || []);
      setInvoices(i.data.items || []);
      setJobs(j.data.items || []);
    } catch { toast.error('Could not load your data.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const payInvoice = (inv) => {
    if (!inv.public_token) { toast.error('Payment link unavailable. Please contact the office.'); return; }
    window.open(`/pay/${inv.public_token}`, '_blank');
  };

  const balanceDue = (inv) => {
    if (inv.balance_due !== undefined && inv.balance_due !== null) return Number(inv.balance_due);
    return Number(inv.total || 0) - Number(inv.amount_paid || 0);
  };

  const Column = ({ icon: Icon, title, count, children, testid }) => (
    <Card className="bg-white border-gray-100 shadow-sm" data-testid={testid}>
      <CardContent className="p-0">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
          <Icon className="w-4 h-4 text-[#3399FF]" />
          <h2 className="text-sm font-bold text-gray-900 font-['Barlow'] uppercase tracking-wide">{title}</h2>
          <span className="ml-auto text-xs font-mono text-gray-400">{count}</span>
        </div>
        <div className="divide-y divide-gray-50">{children}</div>
      </CardContent>
    </Card>
  );

  const Empty = ({ label }) => <div className="px-5 py-10 text-center text-sm text-gray-400">{label}</div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="contractor-portal">
      {/* Header */}
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#003366] to-[#3399FF] flex items-center justify-center">
              <Droplets className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-sm font-bold font-['Barlow'] leading-none">Blue Well Drilling</h1>
              <p className="text-[11px] text-slate-400 mt-0.5">Contractor Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium leading-none">{user?.first_name} {user?.last_name}</p>
              <p className="text-[11px] text-slate-400 mt-0.5 capitalize">{user?.role}</p>
            </div>
            <button data-testid="portal-logout-btn" onClick={handleLogout} className="text-slate-300 hover:text-red-400 transition-colors p-2 rounded-lg hover:bg-white/[0.06]">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow']">Welcome{user?.first_name ? `, ${user.first_name}` : ''}</h2>
          <p className="text-sm text-gray-500 mt-1">Here are your estimates, invoices and jobs with Blue Well Drilling.</p>
        </div>

        {loading ? (
          <div className="py-20 text-center text-gray-400">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Estimates */}
            <Column icon={FileText} title="Estimates" count={estimates.length} testid="portal-estimates">
              {estimates.length === 0 ? <Empty label="No estimates yet." /> : estimates.map((e) => (
                <div key={e.id} data-testid={`portal-estimate-${e.id}`} className="px-5 py-3.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-mono text-gray-500">{e.estimate_number}</span>
                    <StatusBadge status={e.status} />
                  </div>
                  <p className="text-sm font-medium text-gray-900 mt-1 truncate">{e.title}</p>
                  <p className="text-sm font-semibold text-gray-700 mt-0.5">{money(e.total)}</p>
                </div>
              ))}
            </Column>

            {/* Invoices */}
            <Column icon={Receipt} title="Invoices" count={invoices.length} testid="portal-invoices">
              {invoices.length === 0 ? <Empty label="No invoices yet." /> : invoices.map((inv) => {
                const due = balanceDue(inv);
                const payable = due > 0 && !['cancelled', 'paid'].includes(inv.status);
                return (
                  <div key={inv.id} data-testid={`portal-invoice-${inv.id}`} className="px-5 py-3.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-mono text-gray-500">{inv.invoice_number}</span>
                      <StatusBadge status={inv.status} />
                    </div>
                    <p className="text-sm font-medium text-gray-900 mt-1 truncate">{inv.title}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <div>
                        <p className="text-sm font-semibold text-gray-700">{money(inv.total)}</p>
                        {due > 0 && <p className="text-[11px] text-amber-600">Balance: {money(due)}</p>}
                      </div>
                      {payable && (
                        <Button data-testid={`portal-pay-${inv.id}`} size="sm" onClick={() => payInvoice(inv)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs">
                          <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Pay Now
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </Column>

            {/* Jobs */}
            <Column icon={Wrench} title="Jobs" count={jobs.length} testid="portal-jobs">
              {jobs.length === 0 ? <Empty label="No jobs yet." /> : jobs.map((j) => (
                <div key={j.id} data-testid={`portal-job-${j.id}`} className="px-5 py-3.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-mono text-gray-500">{j.job_number}</span>
                    <StatusBadge status={j.status} />
                  </div>
                  <p className="text-sm font-medium text-gray-900 mt-1 truncate">{j.title}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">Scheduled: {fmtDate(j.scheduled_date)}</p>
                </div>
              ))}
            </Column>
          </div>
        )}
      </main>
    </div>
  );
}
